import { add, allocateEvenly, money, subtract, sum, zero, type Money } from './money.js';

/**
 * Invoice and patient-account arithmetic (spec §10).
 *
 * Nothing here reads a stored "total". Every figure is derived from lines,
 * allocations and credit notes, so a balance can never drift away from the
 * transactions that produced it.
 */

export interface LineInput {
  quantity: number;
  unitPriceMinor: number;
  discountMinor: number;
  taxMinor: number;
  currency: string;
}

export function lineTotal(line: LineInput): Money {
  const gross = line.quantity * line.unitPriceMinor;
  return money(gross - line.discountMinor + line.taxMinor, line.currency);
}

export interface InvoiceTotals {
  subtotal: Money;
  discount: Money;
  tax: Money;
  total: Money;
}

export function invoiceTotals(lines: readonly LineInput[], currency: string): InvoiceTotals {
  const subtotal = sum(
    lines.map((l) => money(l.quantity * l.unitPriceMinor, l.currency)),
    currency,
  );
  const discount = sum(lines.map((l) => money(l.discountMinor, l.currency)), currency);
  const tax = sum(lines.map((l) => money(l.taxMinor, l.currency)), currency);
  return { subtotal, discount, tax, total: add(subtract(subtotal, discount), tax) };
}

export interface InvoiceBalanceInput {
  currency: string;
  lines: readonly LineInput[];
  /** Payments allocated to this invoice. */
  allocatedMinor: number;
  /** Credit notes posted against it. */
  creditedMinor: number;
}

export interface InvoiceBalance {
  total: Money;
  credited: Money;
  /** What the patient is actually expected to pay. */
  payable: Money;
  paid: Money;
  outstanding: Money;
  settled: boolean;
  overpaid: boolean;
}

export function invoiceBalance(input: InvoiceBalanceInput): InvoiceBalance {
  const { total } = invoiceTotals(input.lines, input.currency);
  const credited = money(input.creditedMinor, input.currency);
  const paid = money(input.allocatedMinor, input.currency);
  const payable = subtract(total, credited);
  const outstanding = subtract(payable, paid);
  return {
    total,
    credited,
    payable,
    paid,
    outstanding,
    settled: outstanding.minor <= 0,
    // Recorded honestly rather than clamped to zero: an overpayment is a real
    // thing that needs a refund or a credit, not a rounding artefact.
    overpaid: outstanding.minor < 0,
  };
}

/**
 * Age receivables into the usual buckets. `asOf` is passed in rather than read
 * from the clock so a report is reproducible.
 */
export type AgeBucket = 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90_plus';

export function ageBucket(dueAt: Date | null, asOf: Date): AgeBucket {
  if (!dueAt) return 'current';
  const days = Math.floor((asOf.getTime() - dueAt.getTime()) / 86_400_000);
  if (days <= 0) return 'current';
  if (days <= 30) return 'd1_30';
  if (days <= 60) return 'd31_60';
  if (days <= 90) return 'd61_90';
  return 'd90_plus';
}

/**
 * Build an installment schedule. Parts sum EXACTLY to the total (largest
 * remainder), and each is an EXPECTED payment — never a recorded receipt.
 */
export interface ScheduledInstallment {
  sequence: number;
  dueOn: string;
  amount: Money;
}

export function buildInstallments(
  total: Money,
  count: number,
  firstDueOn: Date,
  intervalDays = 30,
): ScheduledInstallment[] {
  const parts = allocateEvenly(total, count);
  return parts.map((amount, i) => {
    const due = new Date(firstDueOn.getTime() + i * intervalDays * 86_400_000);
    return {
      sequence: i + 1,
      dueOn: due.toISOString().slice(0, 10),
      amount,
    };
  });
}

/**
 * Allocate a payment across outstanding invoices, oldest first, without ever
 * over-allocating. Returns the allocations and anything left unapplied (which
 * becomes a patient credit rather than disappearing).
 */
export interface AllocatableInvoice {
  invoiceId: string;
  outstandingMinor: number;
  issuedAt: Date | null;
}

export function allocatePayment(
  amount: Money,
  invoices: readonly AllocatableInvoice[],
  /**
   * The invoice the patient is paying FOR. It is satisfied first; whatever is
   * left still flows to the others oldest-first. Without this the sort below
   * quietly put the oldest invoice ahead of the one that was named, and a
   * receipt that said "for invoice 12" paid invoice 3.
   */
  firstInvoiceId: string | null = null,
): { allocations: { invoiceId: string; amount: Money }[]; unapplied: Money } {
  const ordered = [...invoices]
    .filter((i) => i.outstandingMinor > 0)
    .sort((a, b) => {
      if (firstInvoiceId && a.invoiceId !== b.invoiceId) {
        if (a.invoiceId === firstInvoiceId) return -1;
        if (b.invoiceId === firstInvoiceId) return 1;
      }
      return (a.issuedAt?.getTime() ?? 0) - (b.issuedAt?.getTime() ?? 0);
    });

  let remaining = amount.minor;
  const allocations: { invoiceId: string; amount: Money }[] = [];
  for (const inv of ordered) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, inv.outstandingMinor);
    allocations.push({ invoiceId: inv.invoiceId, amount: money(take, amount.currency) });
    remaining -= take;
  }
  return { allocations, unapplied: money(remaining, amount.currency) };
}

/**
 * The distinct figures a report must never conflate (spec §10). Each is
 * labelled so a caller cannot accidentally present one as another.
 */
export interface PeriodFigures {
  billed: Money;
  collected: Money;
  outstanding: Money;
  expenses: Money;
  labCosts: Money;
  commissions: Money;
}

export function emptyFigures(currency: string): PeriodFigures {
  return {
    billed: zero(currency),
    collected: zero(currency),
    outstanding: zero(currency),
    expenses: zero(currency),
    labCosts: zero(currency),
    commissions: zero(currency),
  };
}

/**
 * A deliberately conservative profitability estimate, and it is named an
 * ESTIMATE because it is one: it uses cash collected minus recorded costs in
 * the same period, which is not the same as accrual profit and does not include
 * costs that have not been entered.
 */
export function estimatedProfit(f: PeriodFigures): Money {
  return subtract(subtract(subtract(f.collected, f.expenses), f.labCosts), f.commissions);
}
